import { useCallback, useRef } from 'react'

import { PhoneInputProps } from '@makyoapp/makyo-tw-ui'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useCompBackgroundColor } from '@/context/__project__/CompContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder/types'
import { FORM_VALIDATION_PATTERNS, formatColumnValue } from '@/shared/form-utils'
import { getSafely } from '@/shared/safely'

import { useUpdateFormDefaultValue } from '../../hooks/useUpdateFormDefaultValue'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'

interface CompPhoneInputWrapperCoreProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: PhoneInputProps, ref: React.RefObject<HTMLInputElement>) => React.ReactNode
}

export function CompPhoneInputWrapperCore({
  infos: { settings, containerClient, detailTableId, detailRowIndex, component },
  form,
  children,
}: CompPhoneInputWrapperCoreProps) {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()
  const { currentParentBg } = useCompBackgroundColor()

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.PhoneInputDataValue] as string
  const column = dataSourceTable?.columnsMap?.get(columnId)

  const defaultValue = useParseVariableSelect<string>(
    settings[ComponentSettingKey.PhoneInputDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const isEditable = getSafely(settings, [ComponentSettingKey.PhoneInputDataDefaultValueEditable], true) as boolean

  const min = (settings?.[ComponentSettingKey.PhoneInputBehaviorMinChar] as number) ?? 1
  const max = (settings?.[ComponentSettingKey.PhoneInputBehaviorMaxChar] as number) ?? 16

  const pattern = getSafely(settings, [ComponentSettingKey.PhoneInputBehaviorPattern], 'none') as string
  const showValidationHelpText = getSafely(
    settings,
    [ComponentSettingKey.PhoneInputBehaviorPatternShowHelp],
    true
  ) as boolean
  const defaultHelpText = showValidationHelpText
    ? FORM_VALIDATION_PATTERNS.find((p) => p.pattern === pattern)?.helpText || ''
    : ''
  const required = getSafely(settings, [ComponentSettingKey.PhoneInputBehaviorRequired], false) as boolean
  const showCharCounter = getSafely(
    settings,
    [ComponentSettingKey.PhoneInputBehaviorCharacterCounter],
    false
  ) as boolean

  const value = formState[columnId] as string | number | undefined
  const inputRef = useRef<HTMLInputElement>(null)

  const labelData = settings[ComponentSettingKey.PhoneInputDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)

  const handleUpdateDefaultValue = useCallback(
    (defValue: string | number | undefined) => {
      if (!columnId) return

      const newValue = formatColumnValue(defValue, column)
      if (inputRef.current) {
        inputRef.current.value = newValue
      }
      setFormState((prev) => ({ ...prev, [columnId]: newValue }))
    },
    [columnId, column, setFormState]
  )

  useUpdateFormDefaultValue({
    defaultValue: formatColumnValue(defaultValue, column),
    value: formState[columnId],
    onUpdate: handleUpdateDefaultValue,
  })

  const handleChange = (value: string) => {
    if (value.length > max) {
      return
    }
    setFormState((prev) => ({ ...prev, [columnId]: value }))
  }

  const phoneInputFieldProps: PhoneInputProps = {
    value: value as string,
    onChange: handleChange,
    label,
    disabled: !isEditable,
    backgroundColor: currentParentBg,
    maxCharCount: max,
    minCharCount: min,
    supportingText: defaultHelpText,
    required,
    charCounter: showCharCounter,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(phoneInputFieldProps, inputRef)}
        <CompOverlayDevmode handleClick={() => openComponentSettings(component)} />
      </div>
    )
  }

  return children(phoneInputFieldProps, inputRef)
}
